import { z } from 'zod';
import type { PlanCode, PlanQuotaKey } from './plan';
import type { TenantStorageStatus } from './storage';

export const SettingsLocale = z.enum(['zh-CN']);
export type SettingsLocale = z.infer<typeof SettingsLocale>;

export const SettingsTimezone = z.enum(['Asia/Shanghai', 'UTC']);
export type SettingsTimezone = z.infer<typeof SettingsTimezone>;

export const SettingsUpdateRequest = z
  .object({
    display_name: z.string().trim().min(1).max(64).optional(),
    tenant_name: z.string().trim().min(1).max(80).optional(),
    locale: SettingsLocale.optional(),
    timezone: SettingsTimezone.optional(),
    competitor_auto_analysis_enabled: z.boolean().optional(),
    aigc_generation_default_model_id: z.string().trim().min(1).max(128).nullable().optional(),
    competitor_insight_default_model_id: z.string().trim().min(1).max(128).nullable().optional(),
    self_note_analysis_default_model_id: z.string().trim().min(1).max(128).nullable().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one setting must be provided',
  });
export type SettingsUpdateRequest = z.infer<typeof SettingsUpdateRequest>;

export type SettingsQuotaKey = PlanQuotaKey;

export type SettingsQuota = {
  key: SettingsQuotaKey;
  used: number | null;
  limit: number;
  reset_at: string | null;
  updated_at: string | null;
};

export type SettingsProfile = {
  user: {
    id: string;
    email: string;
    display_name: string;
    avatar_url: string | null;
    email_verified_at: string | null;
    locale: SettingsLocale;
    timezone: SettingsTimezone;
    created_at: string;
    updated_at: string;
  };
  tenant: {
    id: string;
    name: string;
    slug: string;
    type: 'personal' | 'team' | 'mcn';
    status: 'active' | 'suspended' | 'deleted';
    created_at: string;
    updated_at: string;
  };
  plan: {
    code: PlanCode;
    display_name: string;
    price_monthly_cents: number;
    price_yearly_cents: number;
    currency: string;
    model_tier: 'lite' | 'standard' | 'premium' | 'all';
    storage_mb: number;
    daily_recommendation: {
      auto_refresh_hours: number;
    };
  };
  quotas: SettingsQuota[];
  storage: TenantStorageStatus;
  preferences: {
    competitor_auto_analysis_enabled: boolean;
    aigc_generation_default_model_id: string | null;
    competitor_insight_default_model_id: string | null;
    self_note_analysis_default_model_id: string | null;
    can_manage_ai_model_defaults: boolean;
  };
};

export type SettingsResponse = SettingsProfile;
